import axiosInstance from 'services/axiosInstance';

export const POPUP_TICKET_FRAGMENT = 'cs-ticket';
export const POPUP_TICKET_GLOBAL = '__msppPopupTicket';

const TICKET_PATTERN = /^[A-Za-z0-9_-]{43}$/;

export const REDEEM_RETRY_DELAYS_MS: readonly number[] = [250, 500, 1000, 1500, 2000, 2250, 2500];

export interface IPopupSession {
  accessToken: string;
  userId: string;
  username: string;
}

interface IRedeemResponse {
  accessToken?: string;
  userId?: string | number;
  username?: string;
}

interface IRedeemOptions {
  delaysMs?: readonly number[];
  sleep?: (ms: number) => Promise<void>;
}

const base64Url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export const isPopupTicket = (value: unknown): value is string =>
  typeof value === 'string' && TICKET_PATTERN.test(value);

export const newPopupTicket = (): string => base64Url(crypto.getRandomValues(new Uint8Array(32)));

export const challengeFor = async (verifier: string): Promise<string> =>
  base64Url(
    new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))),
  );

export const withPopupTicket = (url: string, ticket: string): string => {
  const next = new URL(url);
  next.hash = `${POPUP_TICKET_FRAGMENT}=${ticket}`;
  return next.toString();
};

export const takeStrippedPopupTicket = (): string | null => {
  const stashed: unknown = Reflect.get(window, POPUP_TICKET_GLOBAL);
  Reflect.deleteProperty(window, POPUP_TICKET_GLOBAL);
  return isPopupTicket(stashed) ? stashed : null;
};

export const isOpenedByPod = (): boolean => {
  try {
    return document.referrer !== '' && new URL(document.referrer).origin === window.location.origin;
  } catch {
    return false;
  }
};

export const registerPopupTicket = async (ticket: string): Promise<void> => {
  try {
    const challenge = await challengeFor(ticket);
    await axiosInstance.post(
      '/Auth/popup-handoff',
      { challenge },
      { skipErrorSnackbar: true } as object,
    );
  } catch {
    return undefined;
  }
};

const wait = (ms: number): Promise<void> =>
  new Promise(resolve => { window.setTimeout(resolve, ms); });

const retryable = (err: unknown): boolean => {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  return status === undefined || status === 400 || status >= 500;
};

const sessionFrom = (data: IRedeemResponse | undefined): IPopupSession | null => {
  const accessToken = data?.accessToken;
  const userId = data?.userId;
  if (!accessToken || userId === undefined || userId === null || userId === '') return null;
  return { accessToken, userId: String(userId), username: data?.username ?? '' };
};

export const redeemPopupTicket = async (
  ticket: string,
  { delaysMs = REDEEM_RETRY_DELAYS_MS, sleep = wait }: IRedeemOptions = {},
): Promise<IPopupSession | null> => {
  for (let attempt = 0; attempt <= delaysMs.length; attempt++) {
    try {
      const response = await axiosInstance.post<IRedeemResponse>(
        '/Auth/popup-handoff/redeem',
        { verifier: ticket },
        { skipErrorSnackbar: true, rejectWithResponse: true } as object,
      );
      return sessionFrom(response?.data);
    } catch (err) {
      if (!retryable(err) || attempt === delaysMs.length) return null;
      await sleep(delaysMs[attempt]);
    }
  }
  return null;
};
